import {
  ChangeDetectionStrategy,
  Component,
  NgZone,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { LocationInitialUrl } from '@freelancer/location';
import { isDefined } from '@freelancer/utils';
import * as Rx from 'rxjs';
import { debounceTime, filter, map, startWith, take } from 'rxjs/operators';
import { v4 as uuidv4 } from 'uuid';
import {
  getCLS,
  getFCP,
  getFID,
  getLCP,
  Metric,
  PerformanceEventTiming,
} from 'web-vitals';
import { Tracking } from './tracking.service';

// https://w3c.github.io/largest-contentful-paint/#sec-largest-contentful-paint-interface
// https://developer.mozilla.org/en-US/docs/Web/API/LargestContentfulPaint
// FIXME: This type definition isn't provided by TypeScript yet
interface LargestContentfulPaint {
  element?: Element;
  url?: string;
}

// https://wicg.github.io/layout-instability/#sec-layout-shift
// https://developer.mozilla.org/en-US/docs/Web/API/LayoutShift
// FIXME: This type definition isn't provided by TypeScript yet
interface LayoutShift extends PerformanceEntry {
  sources?: LayoutShiftAttribution[];
  value: number;
}

// https://wicg.github.io/layout-instability/#sec-layout-shift-attribution
// https://developer.mozilla.org/en-US/docs/Web/API/LayoutShiftAttribution
// FIXME: This type definition isn't provided by TypeScript yet
interface LayoutShiftAttribution {
  node?: Node;
}

@Component({
  selector: 'fl-core-web-vitals-tracking',
  template: ` <ng-container></ng-container> `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CoreWebVitalsTrackingComponent implements OnDestroy, OnInit {
  coreWebVitalsSubscription?: Rx.Subscription;
  hasReportedInitial = false;
  spaSessionId = uuidv4();

  constructor(
    private ngZone: NgZone,
    private t: Tracking,
    private locationInitialUrl: LocationInitialUrl, // private swUpdate: SwUpdate,
  ) {}

  ngOnInit() {
    const isPwaPromise =
      window.navigator && window.navigator.serviceWorker
        ? window.navigator.serviceWorker
            .getRegistration('/')
            .then(registration => registration?.active?.state === 'activated')
        : Promise.resolve(false);

    this.ngZone.runOutsideAngular(async () => {
      const lcp$ = new Rx.Observable<Metric>(observer => {
        // Reports final on `keydown`, `pointerdown`, `scroll`
        // or `visibilitychange` becomes `hidden`
        // Note that this won't report anything if page is hidden
        // before API initializes
        getLCP((metric: Metric) => {
          observer.next(metric);
          observer.complete();
        });
      });

      const fid$ = new Rx.Observable<Metric>(observer => {
        // Reports final on `keydown`, `pointerdown`
        // Note that this won't report anything if page is hidden
        // before API initializes
        getFID((metric: Metric) => {
          observer.next(metric);
          observer.complete();
        });
      });

      const fcp$ = new Rx.Observable<Metric>(observer => {
        // Reports final on `keydown`, `pointerdown`
        // Note that this won't report anything if page is hidden
        // before API initializes
        getFCP((metric: Metric) => {
          observer.next(metric);
          observer.complete();
        });
      });

      const cls$ = new Rx.Observable<Metric>(observer => {
        // Report all changes
        getCLS((metric: Metric) => {
          observer.next(metric);
        }, true);
      });

      // Fallback if user did not interact with page before page is hidden
      const fidOrHidden$ = Rx.merge(
        fid$,
        Rx.fromEvent(document, 'visibilitychange').pipe(
          filter(() => document.visibilityState === 'hidden'),
          map(() => undefined),
          take(1),
        ),
      ).pipe(take(1));

      // Wait for all three core web vitals before tracking on initial page load
      this.coreWebVitalsSubscription = Rx.combineLatest([
        fidOrHidden$,
        // Initial value for LCP and CLS needed for unsupported browsers
        lcp$.pipe(startWith(undefined)),
        cls$.pipe(
          startWith(undefined),
          // Can fire frequently for highly dynamic views
          debounceTime(500),
        ),
        fcp$.pipe(startWith(undefined)),
      ]).subscribe(async ([fid, lcp, cls, fcp]) => {
        // Don't track if we have no metrics, it's possible
        // if page is loaded in the background
        if (!fid && !lcp && !cls && !fcp) {
          return;
        }

        // Report FCP, LCP and FID in one event
        const hasInitialMetrics = fid && fcp && lcp;
        if (!hasInitialMetrics && !cls) {
          return;
        }

        this.t.track('core_web_vitals_timings', {
          fid:
            this.hasReportedInitial || !hasInitialMetrics
              ? undefined
              : fid?.delta, // in ms
          lcp:
            this.hasReportedInitial || !hasInitialMetrics
              ? undefined
              : lcp?.delta, // in ms
          fcp:
            this.hasReportedInitial || !hasInitialMetrics
              ? undefined
              : fcp?.delta,
          cls: cls?.value, // take current value since it changes over time
          lcpElement: this.hasReportedInitial
            ? undefined
            : lcp
            ? this.getLcpElement(lcp)
            : undefined,
          clsElements: cls ? this.getClsElements(cls) : undefined,
          fidElement: this.hasReportedInitial
            ? undefined
            : fid
            ? this.getFidElement(fid)
            : undefined,
          isPwa: await isPwaPromise,
          initialUrl: this.locationInitialUrl.getInitialUrl(),
          spaSessionId: this.spaSessionId,
        });

        // Report FCP, LCP and FID once
        if (hasInitialMetrics) {
          this.hasReportedInitial = true;
        }
      });
    });
  }

  private getLcpElement(metric: Metric) {
    // get the latest LCP element
    const entry = metric.entries[
      metric.entries.length - 1
    ] as LargestContentfulPaint;
    const element = this.stripGeneratedAttributesAndClasses(entry?.element);
    return element ? element.outerHTML?.substring(0, 1000) : entry.url;
  }

  private getClsElements(metric: Metric) {
    const entries = metric.entries as LayoutShift[];
    // Since we limit the payload size, we prioritize worst offenders
    entries.sort((a, b) => b.value - a.value);
    return entries
      .map((entry: LayoutShift) => {
        const sources = entry.sources?.map((source: LayoutShiftAttribution) => {
          let elementString: string | undefined;
          if (this.nodeIsElement(source.node)) {
            const element = this.stripGeneratedAttributesAndClasses(
              source.node,
            );
            elementString = element?.outerHTML;
          } else {
            elementString = source.node?.textContent ?? undefined; // force null to be undefined
          }

          return elementString?.substring(0, 1000);
        });
        return `${entry.value} ${(sources || []).join(' ')}`;
      })
      .join(' ')
      .substring(0, 2000);
  }

  private getFidElement(metric: Metric): string | undefined {
    const entry = metric.entries[
      metric.entries.length - 1
    ] as PerformanceEventTiming;
    const target = this.stripGeneratedAttributesAndClasses(entry?.target);

    return target?.outerHTML.substring(0, 1000);
  }

  private stripGeneratedAttributesAndClasses(
    element?: Element,
  ): Element | undefined {
    const attributes = element?.getAttributeNames();

    // Make a copy so that we don't directly modify the original element
    const elementCopy = element?.cloneNode(true) as Element;

    // FIXME: This operates on the current element only.
    // Child nodes aren't affected
    attributes?.forEach(attribute => {
      if (attribute.match(/^_ng(?:host|content)-webapp-c(?:\d+)$/)) {
        elementCopy?.removeAttribute(attribute);
      }
    });

    elementCopy?.classList.remove('ng-star-inserted');

    return elementCopy;
  }

  private nodeIsElement(node?: Node): node is Element {
    return isDefined((node as Element)?.getAttributeNames);
  }

  ngOnDestroy() {
    if (this.coreWebVitalsSubscription) {
      this.coreWebVitalsSubscription.unsubscribe();
    }
  }
}
